import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, Check, Trash2, RotateCcw, Loader2, AtSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ProjectCommentsDialog — threaded async comments for a project.
 * Props: open, onOpenChange, projectId, projectName, currentSlideIndex (optional)
 */
export default function ProjectCommentsDialog({
  open, onOpenChange, projectId, projectName, currentSlideIndex = null,
}) {
  const [comments, setComments] = useState([]);
  const [mentionable, setMentionable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState("");
  const [attachToSlide, setAttachToSlide] = useState(true);
  const [posting, setPosting] = useState(false);
  const [includeResolved, setIncludeResolved] = useState(true);

  // Mention autocomplete state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef(null);

  const auth = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/comments`, {
          params: { include_resolved: includeResolved },
          headers: auth(),
        }),
        axios.get(`${API}/projects/${projectId}/comments/mentionable`, { headers: auth() })
          .catch(() => ({ data: { users: [] } })),
      ]);
      setComments(c.data?.comments || []);
      setMentionable(m.data?.users || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load comments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, includeResolved]);

  const post = async () => {
    if (!newText.trim()) return;
    setPosting(true);
    try {
      const body = { text: newText.trim() };
      if (attachToSlide && currentSlideIndex !== null && currentSlideIndex !== undefined) {
        body.slide_index = currentSlideIndex;
      }
      await axios.post(`${API}/projects/${projectId}/comments`, body, { headers: auth() });
      setNewText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (commentId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/comments/${commentId}`, { headers: auth() });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const toggleResolved = async (comment) => {
    try {
      await axios.patch(
        `${API}/projects/${projectId}/comments/${comment.comment_id}`,
        { resolved: !comment.resolved },
        { headers: auth() },
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  const fmt = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  // Detect an in-progress @mention right before the caret.
  const handleTextChange = (e) => {
    const val = e.target.value;
    setNewText(val);
    const caret = e.target.selectionStart ?? val.length;
    const before = val.slice(0, caret);
    const m = before.match(/@([A-Za-z0-9_.\-]{0,64})$/);
    if (m) {
      setMentionQuery(m[1].toLowerCase());
      setMentionOpen(true);
      setMentionIndex(0);
    } else {
      setMentionOpen(false);
    }
  };

  const filteredMentions = mentionable.filter((u) =>
    (u.mention_key || "").toLowerCase().startsWith(mentionQuery)
  ).slice(0, 6);

  const insertMention = (u) => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? newText.length;
    const before = newText.slice(0, caret);
    const after = newText.slice(caret);
    const newBefore = before.replace(/@([A-Za-z0-9_.\-]{0,64})$/, `@${u.mention_key} `);
    const next = newBefore + after;
    setNewText(next);
    setMentionOpen(false);
    setTimeout(() => {
      el.focus();
      const pos = newBefore.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!mentionOpen || filteredMentions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => (i + 1) % filteredMentions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(filteredMentions[mentionIndex]); }
    else if (e.key === "Escape") { setMentionOpen(false); }
  };

  // Highlight @mentions in comment text — matches keys resolved against the
  // current mentionable list. Falls back to plain text if no match.
  const renderCommentText = (text, mentionedUserIds = []) => {
    const parts = [];
    let last = 0;
    const re = /@([A-Za-z0-9_.\-]{2,64})/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      const key = m[1].toLowerCase();
      const user = mentionable.find((u) => u.mention_key === key);
      const isResolved = user && mentionedUserIds.includes(user.user_id);
      if (isResolved) {
        parts.push(
          <span key={m.index} className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-medium">
            @{user.name || user.email || key}
          </span>,
        );
      } else {
        parts.push(m[0]);
      }
      last = re.lastIndex;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-xl" data-testid="comments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-amber-500" />
            Comments · {projectName || "Project"}
          </DialogTitle>
          <DialogDescription>
            Async feedback for you and your collaborators. Editors and viewers can all comment.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{comments.length} {comments.length === 1 ? "comment" : "comments"}</span>
          <button
            onClick={() => setIncludeResolved((v) => !v)}
            className="hover:text-slate-200 underline decoration-dotted"
            data-testid="comments-toggle-resolved"
          >
            {includeResolved ? "Hide resolved" : "Show resolved"}
          </button>
        </div>

        <ScrollArea className="max-h-[380px] pr-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              No comments yet. Start the conversation below.
            </div>
          ) : (
            <ul className="space-y-3">
              {comments.map((c) => (
                <li
                  key={c.comment_id}
                  className={`rounded-lg border border-slate-800 p-3 ${c.resolved ? "opacity-60 bg-slate-950/40" : "bg-slate-950/40"}`}
                  data-testid={`comment-${c.comment_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs text-slate-300">
                        {(c.author_name || c.author_email || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <span className="text-slate-200 font-medium">{c.author_name || c.author_email}</span>
                      {c.slide_index !== null && c.slide_index !== undefined && (
                        <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">
                          slide {c.slide_index + 1}
                        </Badge>
                      )}
                      {c.resolved && (
                        <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px]">
                          <Check className="w-3 h-3 mr-1" /> resolved
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleResolved(c)}
                        className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400"
                        title={c.resolved ? "Reopen" : "Resolve"}
                        data-testid={`comment-resolve-${c.comment_id}`}
                      >
                        {c.resolved ? <RotateCcw className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => remove(c.comment_id)}
                        className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-400"
                        title="Delete"
                        data-testid={`comment-delete-${c.comment_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-200 mt-2 whitespace-pre-wrap leading-relaxed">
                    {renderCommentText(c.text, c.mentioned_user_ids || [])}
                  </p>
                  <div className="text-[10px] text-slate-500 mt-1">{fmt(c.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>

        {/* Composer */}
        <div className="border-t border-slate-800 pt-3 space-y-2 relative">
          <Textarea
            ref={textareaRef}
            value={newText}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment… type @ to mention someone"
            rows={2}
            className="bg-slate-950 border-slate-800 resize-none"
            data-testid="comment-input"
          />

          {/* @mention autocomplete popover */}
          {mentionOpen && filteredMentions.length > 0 && (
            <div
              className="absolute bottom-[70px] left-0 right-0 mx-2 z-50 rounded-md border border-slate-700 bg-slate-950 shadow-2xl overflow-hidden"
              data-testid="mention-popover"
            >
              {filteredMentions.map((u, i) => (
                <button
                  key={u.user_id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${
                    i === mentionIndex ? "bg-amber-500/10 text-amber-200" : "text-slate-200 hover:bg-slate-900"
                  }`}
                  data-testid={`mention-suggestion-${u.user_id}`}
                >
                  <AtSign className="w-3.5 h-3.5 text-slate-500" />
                  <span className="font-medium">{u.name || u.email}</span>
                  <span className="text-xs text-slate-500 ml-auto">{u.role}</span>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            {currentSlideIndex !== null && currentSlideIndex !== undefined ? (
              <label className="flex items-center gap-2 text-xs text-slate-400 select-none">
                <input
                  type="checkbox"
                  checked={attachToSlide}
                  onChange={(e) => setAttachToSlide(e.target.checked)}
                  className="rounded border-slate-700"
                />
                Attach to slide {currentSlideIndex + 1}
              </label>
            ) : (
              <span />
            )}
            <Button
              onClick={post}
              disabled={posting || !newText.trim()}
              className="bg-amber-500 text-black hover:bg-amber-400"
              data-testid="comment-submit-btn"
            >
              {posting ? "…" : <><Send className="w-3.5 h-3.5 mr-2" /> Post</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
